/**
 * DossierSlide · renderer puro de un slide 16:9 del dossier.
 *
 * Extraído de SlidePreview para que también lo use el Modo Presentación
 * (DossierPresentation.jsx). Garantiza que el preview del editor y la
 * presentación en vivo se vean idénticos, y que ambas reflejen lo que el
 * backend PPTX (`dossier_pptx.py`) renderiza al exportar.
 *
 * NOTA: este componente NO añade chrome (header con selector, footer, etc.)
 * — eso lo aporta quien lo monte. El componente sólo dibuja el contenido del
 * slide dentro de su contenedor 16:9.
 *
 * IMPORTANTE: NO toca el editor de guion. Solo dossier.
 */
import { API } from "@/lib/api";

// =================================================================
// Public API
// =================================================================

export const LAYOUT_OPTIONS = [
  { value: "", label: "Automático" },
  { value: "cover", label: "Portada" },
  { value: "chapter-divider", label: "Separador de capítulo" },
  { value: "quote", label: "Cita" },
  { value: "text", label: "Texto solo" },
  { value: "text-narrow", label: "Texto centrado (libro)" },
  { value: "two-cols", label: "Dos columnas" },
  { value: "text-image-right", label: "Texto + imagen (der.)" },
  { value: "text-image-left", label: "Texto + imagen (izq.)" },
  { value: "half-split", label: "Mitad / mitad" },
  { value: "portrait-quote", label: "Retrato + cita" },
  { value: "director-statement", label: "Nota del director" },
  { value: "team-individual", label: "Miembro del equipo" },
  { value: "stats-grid", label: "Ficha técnica (stats)" },
  { value: "grid-2x2", label: "Grid de imágenes 2×2" },
  { value: "fullbleed", label: "Imagen a sangre" },
  { value: "mood-photo", label: "Foto silenciosa (sin texto)" },
  { value: "items", label: "Fichas (cards)" },
  // ── Nuevas plantillas (Feb 2026) ──────────────────────────────
  { value: "timeline", label: "Línea de tiempo (hitos)" },
  { value: "comparison", label: "Comparativa (2 cajas)" },
  { value: "references-grid", label: "Referencias visuales (4 pósters)" },
  { value: "credits-grid", label: "Créditos del equipo (grid)" },
  { value: "data-callout", label: "Cifra protagonista" },
];

export const LAYOUT_LABELS = Object.fromEntries(
  LAYOUT_OPTIONS.map((o) => [o.value || "auto", o.label]),
);

/**
 * Motor de auto-selección de plantilla basado en EL CONTENIDO REAL.
 *
 * Diseñado tras auditoría de 112 slides reales de 4 dossiers profesionales
 * + los 8 dossiers de producción de Grabarte.
 *
 * Reglas duras:
 *   - section.key es HINT suave, no decisor
 *   - Layouts de baja capacidad (cover, quote, fullbleed) sólo se eligen
 *     si el texto es genuinamente corto. Si es largo, vamos a layouts
 *     de mayor capacidad porque el sistema PAGINARÁ automáticamente.
 *   - Imagen + texto largo → text-image-right (capacidad alta, lectura cómoda)
 *   - Sin imagen + texto largo → two-cols o text-narrow
 */
export function resolveAuto(section) {
  const key = (section?.key || "").toLowerCase();
  const text = section?.text || "";
  const textLen = text.length;
  const imgs = section?.images || [];
  const nImgs = imgs.length;
  const items = section?.items || [];
  const nItems = items.length;

  // ============================================================
  // 1) ITEMS (cards) — personajes, referencias, equipo, etc.
  // ============================================================
  if (nItems > 0) return "items";

  // ============================================================
  // 2) Cover (titulo/portada explícito)
  // ============================================================
  if (key === "titulo" || key === "portada" || key.includes("cover")) return "cover";

  // ============================================================
  // 3) Texto MUY CORTO (≤180c) — entra en quote o cover
  // ============================================================
  if (textLen > 0 && textLen <= 180) {
    if (nImgs >= 1) return "portrait-quote";
    return "quote";
  }

  // ============================================================
  // 4) Imágenes múltiples → grid
  // ============================================================
  if (nImgs >= 2) return "grid-2x2";

  // ============================================================
  // 5) Hints específicos
  // ============================================================
  // Nota del director: foto + texto en cita firmada
  if ((key.includes("nota") || key.includes("intencion")) &&
      nImgs >= 1 && textLen >= 200 && textLen <= 850) {
    return "director-statement";
  }
  // Logline o tagline (siempre corto)
  if (key.includes("logline") || key.includes("tagline")) {
    return nImgs >= 1 ? "portrait-quote" : "quote";
  }

  // ============================================================
  // 6) TEXTO SIN IMAGEN
  // ============================================================
  if (nImgs === 0) {
    if (textLen === 0) return "text";
    if (textLen <= 500) return "text";            // breve
    if (textLen <= 1100) return "text-narrow";    // medio, columna estrecha
    return "two-cols";                             // largo (paginará en cols)
  }

  // ============================================================
  // 7) TEXTO + 1 IMAGEN — el caso más común
  //    Para textos LARGOS preferimos text-image-right (capacidad alta)
  //    en lugar de fullbleed (capacidad baja).
  // ============================================================
  if (textLen <= 350) return "half-split";          // texto medio, 50/50 limpio
  // Texto largo (>350c): siempre text-image-right. El motor de paginación
  // partirá automáticamente si supera la capacidad (750c por slide).
  return "text-image-right";
}

/**
 * Renderer principal. Dibuja el slide elegido dentro de su contenedor 16:9.
 * El padre se encarga del tamaño (aspect-video o equivalente).
 */
export default function DossierSlide({ section, project, layout, slideIndex, slideTotal, showWatermark = true }) {
  // Migración: el layout "magazine-spread" fue retirado en Feb 2026. Lo
  // redirigimos a "text-image-right" para que proyectos legacy con ese layout
  // siguan renderizando bien (el motor de paginación se encarga del texto largo).
  let effective = layout || resolveAuto(section);
  if (effective === "magazine-spread") effective = "text-image-right";
  const renderers = {
    cover: <Cover project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "chapter-divider": <ChapterDivider project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    quote: <Quote project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    text: <TextOnly project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "text-narrow": <TextNarrow project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "two-cols": <TwoCols project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "text-image-right": <TextImage project={project} section={section} side="right" pageRef={slideIndex} showWatermark={showWatermark} />,
    "text-image-left": <TextImage project={project} section={section} side="left" pageRef={slideIndex} showWatermark={showWatermark} />,
    "half-split": <HalfSplit project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "portrait-quote": <PortraitQuote project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "director-statement": <DirectorStatement project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "team-individual": <TeamIndividual project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "stats-grid": <StatsGrid project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "grid-2x2": <Grid project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    fullbleed: <Fullbleed project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "mood-photo": <MoodPhoto project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    items: <Cards project={project} section={section} pageRef={slideIndex} slideTotal={slideTotal} showWatermark={showWatermark} />,
    timeline: <Timeline project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    comparison: <Comparison project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "references-grid": <ReferencesGrid project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "credits-grid": <CreditsGrid project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
    "data-callout": <DataCallout project={project} section={section} pageRef={slideIndex} showWatermark={showWatermark} />,
  };
  return renderers[effective] || renderers.text;
}

// =================================================================
// Helpers de imagen y tipografía
// =================================================================

function imgUrl(img) {
  if (!img) return "";
  const u = img.url || img;
  return u.startsWith("data:") ? u : `${API.replace("/api", "")}${u}`;
}

function titleSize(text) {
  const len = (text || "").length;
  if (len <= 12) return "text-[3.2cqw]";
  if (len <= 20) return "text-[2.6cqw]";
  if (len <= 30) return "text-[2cqw]";
  return "text-[1.6cqw]";
}

function pageStr(n) {
  if (!n && n !== 0) return "—";
  return String(n).padStart(2, "0");
}

// =================================================================
// Chrome compartido — eyebrow, rule, footer
// =================================================================

function Chrome({ children }) {
  return <div className="absolute inset-0 bg-[#FAFAF6]">{children}</div>;
}

function Eyebrow({ children, light, accent = "#941620" }) {
  return (
    <p className="font-display text-[0.65cqw] font-semibold uppercase tracking-[0.45em]"
       style={{ color: light ? "rgba(255,255,255,0.85)" : accent }}>
      {children}
    </p>
  );
}

function Rule({ light }) {
  return (
    <div className="mt-[1.2cqw] h-[1px] w-[5%]"
         style={{ background: light ? "rgba(255,255,255,0.65)" : "#941620" }} />
  );
}

function Footer({ project, light, pageRef, showWatermark = true }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-[5%] py-[1.5%]">
      {showWatermark ? (
        <div className="flex items-center gap-[1cqw] whitespace-nowrap">
          <img
            src={light ? "/brand/isotype-white.png" : "/brand/isotype.png"}
            alt=""
            className="h-[1.6cqw] w-auto opacity-50"
            style={{ filter: light ? "none" : "saturate(0.3)" }}
          />
          <p className="font-display text-[0.62cqw] font-semibold uppercase tracking-[0.45em] whitespace-nowrap"
             style={{ color: light ? "rgba(255,255,255,0.55)" : "rgba(23,23,38,0.45)" }}>
            GRABARTE · Audiovisual Studio
          </p>
        </div>
      ) : <span />}
      <p className="font-mono text-[0.55cqw] whitespace-nowrap"
         style={{ color: light ? "rgba(255,255,255,0.55)" : "rgba(23,23,38,0.45)" }}>
        {pageStr(pageRef)}
      </p>
    </div>
  );
}

// =================================================================
// 01 · COVER — imagen a sangre + título grande con vignette
// =================================================================
function Cover({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const titleClass = titleSize(project?.title);
  const eyebrow = `${project?.project_type || "Documental"}${project?.director ? ` · ${project.director}` : ""}`;
  // Sub-título: primera línea del text de la sección. Lo OCULTAMOS si coincide
  // (case-insensitive) con el título del proyecto, para evitar duplicaciones
  // feas en proyectos donde "section.text" repite el título.
  const subline = (section?.text || "").split("\n")[0].trim();
  const titleNorm = (project?.title || "").trim().toLowerCase();
  const subNorm = subline.toLowerCase();
  const showSub = subline && subNorm !== titleNorm;
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#2C2B29]">
      {img ? (
        <img src={imgUrl(img)} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C2B29] via-[#1f1f30] to-[#941620]/40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-[#2C2B29]/85 via-[#2C2B29]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2C2B29]/70 via-transparent to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end px-[6%] pb-[10%] text-white">
        <Eyebrow light accent="#941620">{eyebrow.toUpperCase()}</Eyebrow>
        <h1 className={`mt-[1.5cqw] font-display ${titleClass} font-extrabold uppercase leading-[0.95] tracking-tight`}>
          {project?.title || "Sin título"}
        </h1>
        <Rule light />
        {showSub && (
          <p className="mt-[1.5cqw] max-w-[50%] font-editorial text-[1.05cqw] italic leading-snug text-white/85">
            {subline}
          </p>
        )}
      </div>
      <Footer project={project} light pageRef={pageRef} showWatermark={showWatermark} />
    </div>
  );
}

// =================================================================
// 02 · QUOTE — primera frase centrada con tint
// =================================================================
function Quote({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const raw = (section?.text || "—").trim();
  const m = raw.match(/^[^.!?]+[.!?]/);
  let quote = m ? m[0].trim() : raw;
  if (quote.length > 200) quote = quote.slice(0, 197).trim() + "…";
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#2C2B29]">
      {img && <img src={imgUrl(img)} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-[#2C2B29]/70" />
      <div className="absolute inset-0 flex items-center justify-center px-[12%] text-center">
        <div className="max-w-[80%]">
          <span className="font-display text-[6cqw] font-bold leading-none text-[#941620]/80">&ldquo;</span>
          <p className="-mt-[2cqw] font-editorial text-[1.8cqw] italic leading-[1.35] text-white">{quote}</p>
          <div className="mx-auto mt-[2.5cqw] h-[1px] w-[8%] bg-white/60" />
        </div>
      </div>
      <Footer project={project} light pageRef={pageRef} showWatermark={showWatermark} />
    </div>
  );
}

// =================================================================
// HEAD — eyebrow + título usado por TextOnly / TwoCols / Grid / Cards
// =================================================================
// =================================================================
// HEAD — eyebrow (proyecto) + título grande (NOMBRE DE LA SECCIÓN)
// =================================================================
function Head({ section, project }) {
  const sectionTitle = (section?.label || "").trim() || (project?.title || "");
  return (
    <>
      <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
      <h2 className={`mt-[1cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
        {sectionTitle}
      </h2>
      <Rule />
    </>
  );
}

// =================================================================
// 03 · TEXT ONLY
// =================================================================
function TextOnly({ project, section, pageRef, showWatermark }) {
  return (
    <Chrome>
      <div className="absolute inset-0 px-[6%] py-[7%]">
        <Head section={section} project={project} />
        <p className="mt-[2.5cqw] max-w-[80%] font-editorial text-[0.95cqw] leading-[1.7] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[11]">
          {section?.text || "Sin contenido aún."}
        </p>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 04 · TWO COLS
// =================================================================
function TwoCols({ project, section, pageRef, showWatermark }) {
  const paragraphs = (section?.text || "").split("\n\n");
  const mid = Math.ceil(paragraphs.length / 2);
  const col1 = paragraphs.slice(0, mid).join("\n\n");
  const col2 = paragraphs.slice(mid).join("\n\n");
  return (
    <Chrome>
      <div className="absolute inset-0 px-[6%] py-[7%]">
        <Head section={section} project={project} />
        <div className="mt-[2.5cqw] grid grid-cols-[1fr,1px,1fr] gap-[3%]">
          <p className="font-editorial text-[0.85cqw] leading-[1.65] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[12]">
            {col1 || "—"}
          </p>
          <div className="bg-[#2C2B29]/10" />
          <p className="font-editorial text-[0.85cqw] leading-[1.65] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[12]">
            {col2 || "—"}
          </p>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 05 · TEXT + IMAGE (izq / der) — split limpio 45/55, sin gradient agresivo
// =================================================================
function TextImage({ project, section, side, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const left = side === "left";
  const sectionTitle = (section?.label || "").trim();
  return (
    <Chrome>
      <div className="absolute inset-0 flex">
        <div className={`relative overflow-hidden bg-[#2C2B29] ${left ? "order-1" : "order-2"}`}
             style={{ flex: "0 0 45%" }}>
          {img ? (
            <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-[0.7cqw] uppercase tracking-[0.4em] text-white/30">
              Sin imagen
            </div>
          )}
        </div>
        <div className={`flex flex-col justify-center bg-[#FAFAF6] px-[5cqw] py-[6cqw] ${left ? "order-2" : "order-1"}`}
             style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
          <h2 className={`mt-[0.7cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
            {sectionTitle}
          </h2>
          <Rule />
          <p className="mt-[1.5cqw] font-editorial text-[0.85cqw] leading-[1.7] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[12]">
            {section?.text || "Sin contenido aún."}
          </p>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 10 · MAGAZINE SPREAD — RETIRADA (Feb 2026)
// Los proyectos legacy con layout "magazine-spread" se redirigen a
// "text-image-right" en el switch principal. Mantener este comentario como
// huella histórica para que `git log` y futuros agentes sepan que no fue
// olvidada sino sustituida intencionadamente.
// =================================================================

// =================================================================
// 11 · HALF SPLIT — 50/50 cinematográfico SIN gradient ni texto encima
// =================================================================
function HalfSplit({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const sectionTitle = (section?.label || "").trim();
  return (
    <Chrome>
      <div className="absolute inset-0 flex">
        <div className="relative overflow-hidden bg-[#2C2B29]" style={{ flex: "1 1 0", minWidth: 0 }}>
          {img && <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />}
        </div>
        <div className="flex flex-col justify-center bg-[#FAFAF6] px-[5cqw] py-[6cqw]"
             style={{ flex: "1 1 0", minWidth: 0 }}>
          <Eyebrow accent="#2C2B29">{(project?.title || "").toUpperCase()}</Eyebrow>
          <h2 className={`mt-[1cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
            {sectionTitle}
          </h2>
          <div className="mt-[1.2cqw] h-[2px] w-[12%] bg-[#941620]" />
          <p className="mt-[1.5cqw] font-editorial text-[0.85cqw] leading-[1.7] text-[#2C2B29]/80 whitespace-pre-wrap line-clamp-[12]">
            {section?.text || "Sin contenido aún."}
          </p>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 12 · PORTRAIT QUOTE — foto portrait + cita en mayúsculas grande
// =================================================================
function PortraitQuote({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const sectionTitle = (section?.label || "").trim();
  const raw = (section?.text || "").trim();
  const m = raw.match(/^[^.!?]+[.!?]/);
  let quote = (m ? m[0] : raw).trim();
  if (quote.length > 240) quote = quote.slice(0, 237).trim() + "…";
  const rest = m ? raw.slice(m[0].length).trim() : "";
  return (
    <Chrome>
      <div className="absolute inset-0 flex">
        <div className="relative overflow-hidden bg-[#2C2B29]" style={{ flex: "0 0 33%" }}>
          {img
            ? <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-[#1f1f30] via-[#2C2B29] to-[#3a2f31]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C2B29]/40 to-transparent" />
        </div>
        <div className="flex flex-col justify-center bg-[#FAFAF6] px-[5cqw] py-[6cqw]"
             style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Eyebrow>{sectionTitle.toUpperCase()}</Eyebrow>
          <span className="mt-[0.8cqw] font-display text-[5.5cqw] font-bold leading-[0.7] text-[#941620]/80">&ldquo;</span>
          <p className="mt-[-1cqw] font-display text-[1.7cqw] font-bold uppercase leading-[1.15] tracking-tight text-[#2C2B29]">
            {quote || "Sin contenido aún."}
          </p>
          {rest && (
            <p className="mt-[2cqw] max-w-[88%] font-editorial text-[0.8cqw] leading-[1.65] text-[#2C2B29]/70 whitespace-pre-wrap line-clamp-5">
              {rest}
            </p>
          )}
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 13 · STATS GRID — datos clave del proyecto en grid 2×3
// =================================================================
function StatsGrid({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  // Stats extraídos del proyecto (no de la sección)
  const stats = [
    { label: "Tipo", value: project?.project_type || "—" },
    { label: "Duración", value: project?.duration_min ? `${project.duration_min}'` : "—" },
    { label: "Jornadas", value: project?.shoot_days ? `${project.shoot_days}` : "—" },
    { label: "Director/a", value: project?.director || "—" },
    { label: "Producción", value: project?.producer_person || project?.producer || "—" },
    { label: "Localizaciones", value: project?.locations || "—" },
  ];
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[6%]">
        <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
        <h2 className={`mt-[0.8cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
          {sectionTitle || "Producción"}
        </h2>
        <Rule />
        {section?.text && (
          <p className="mt-[1.5cqw] max-w-[85%] font-editorial text-[0.8cqw] leading-[1.55] italic text-[#2C2B29]/65 line-clamp-3">
            {section.text}
          </p>
        )}
        <div className="mt-[2.2cqw] grid grid-cols-3 gap-[1.5cqw]">
          {stats.map((s, i) => (
            <div key={i} className="rounded-sm border-l-2 border-[#941620] bg-white/80 px-[1.2cqw] py-[1cqw] shadow-sm">
              <p className="font-display text-[0.55cqw] font-semibold uppercase tracking-[0.35em] text-[#2C2B29]/55">
                {s.label}
              </p>
              <p className="mt-[0.5cqw] font-display text-[1.1cqw] font-bold uppercase leading-tight text-[#2C2B29] line-clamp-2">
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 14 · DIRECTOR STATEMENT — cita firmada con foto pequeña
// =================================================================
function DirectorStatement({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  return (
    <Chrome>
      <div className="absolute inset-0 px-[8%] py-[7%]">
        <Eyebrow>{(section?.label || "").toUpperCase()}</Eyebrow>
        <div className="mt-[1cqw] flex items-start gap-[3cqw]">
          {/* Foto pequeña tipo retrato */}
          <div className="shrink-0 overflow-hidden rounded-full bg-[#2C2B29]/10" style={{ width: "8cqw", height: "8cqw" }}>
            {img
              ? <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center text-[0.6cqw] uppercase tracking-[0.3em] text-[#2C2B29]/30">Foto</div>}
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-display text-[4cqw] font-bold leading-[0.5] text-[#941620]/70">&ldquo;</span>
            <p className="mt-[-0.8cqw] font-editorial text-[1cqw] italic leading-[1.65] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[10]">
              {section?.text || "Sin contenido aún."}
            </p>
            <div className="mt-[1.5cqw] flex items-center gap-[1cqw]">
              <div className="h-[1px] w-[5%] bg-[#941620]" />
              <p className="font-display text-[0.7cqw] font-bold uppercase tracking-[0.3em] text-[#2C2B29]">
                {project?.director || "—"}
              </p>
              <span className="text-[0.65cqw] font-semibold uppercase tracking-[0.25em] text-[#2C2B29]/50">
                Director/a
              </span>
            </div>
          </div>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 15 · TEXT NARROW — texto largo centrado tipo libro (para biofilmografía)
// =================================================================
function TextNarrow({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  return (
    <Chrome>
      <div className="absolute inset-0 px-[20%] py-[6%]">
        <div className="text-center">
          <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
          <h2 className={`mt-[1cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
            {sectionTitle}
          </h2>
          <div className="mx-auto mt-[1.2cqw] h-[1px] w-[10%] bg-[#941620]" />
        </div>
        <p className="mt-[2.2cqw] font-editorial text-[0.85cqw] leading-[1.8] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[14]"
           style={{ columnFill: "balance" }}>
          {section?.text || "Sin contenido aún."}
        </p>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 06 · FULL BLEED
// =================================================================
function Fullbleed({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#2C2B29]">
      {img && <img src={imgUrl(img)} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#2C2B29]/85 via-[#2C2B29]/15 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 px-[6%] pb-[7%] text-white">
        <Eyebrow light accent="#941620">{section?.label}</Eyebrow>
        <h2 className={`mt-[1cqw] font-display ${titleSize(project?.title)} font-extrabold uppercase leading-[0.95] tracking-tight`}>
          {project?.title}
        </h2>
        <Rule light />
        {section?.text && (
          <p className="mt-[1.5cqw] max-w-[55%] font-editorial text-[0.95cqw] italic leading-snug text-white/85 line-clamp-3">
            {section.text.split("\n")[0]}
          </p>
        )}
      </div>
      <Footer project={project} light pageRef={pageRef} showWatermark={showWatermark} />
    </div>
  );
}

// =================================================================
// 07 · GRID 2x2
// =================================================================
function Grid({ project, section, pageRef, showWatermark }) {
  const imgs = section?.images || [];
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <div className="grid grid-cols-[1.1fr,2fr] gap-[3%]">
          <div className="flex flex-col">
            <Head section={section} project={project} />
            <p className="mt-[1.5cqw] font-editorial text-[0.8cqw] leading-[1.55] text-[#2C2B29]/80 whitespace-pre-wrap line-clamp-[10]">
              {section?.text || "—"}
            </p>
          </div>
          <div className="grid grid-cols-2 grid-rows-2 gap-[1.5%]">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="relative overflow-hidden rounded-sm bg-[#2C2B29]/8 ring-1 ring-[#2C2B29]/10">
                {imgs[i]
                  ? <img src={imgUrl(imgs[i])} alt="" className="h-full w-full object-cover" />
                  : <div className="flex h-full items-center justify-center text-[0.6cqw] uppercase tracking-[0.3em] text-[#2C2B29]/25">+ {i + 1}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 08 · CARDS — sub-items (personajes / referencias / biofilmo / cartas)
// =================================================================
function Cards({ project, section, pageRef, slideTotal, showWatermark }) {
  const items = (section?.items || []).slice(0, 4);
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <div className="flex items-end justify-between">
          <div>
            <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
            <h2 className={`mt-[0.6cqw] font-display ${titleSize(section?.label)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
              {section?.label || "—"}
            </h2>
          </div>
          <p className="font-mono text-[0.6cqw] text-[#2C2B29]/40">
            {items.length} / {(section?.items || []).length}
          </p>
        </div>
        <Rule />
        <div className="mt-[1.5cqw] grid h-[75%] grid-cols-2 grid-rows-2 gap-[1.5%]">
          {Array.from({ length: 4 }).map((_, i) => {
            const it = items[i];
            if (!it) return <div key={i} className="rounded-sm border border-dashed border-[#2C2B29]/10" />;
            return (
              <div key={i} className="grid grid-cols-[1fr,1.8fr] overflow-hidden rounded-sm bg-white ring-1 ring-[#2C2B29]/10">
                <div className="relative bg-[#F1EEE5]">
                  {it.image_url
                    ? <img src={imgUrl({ url: it.image_url })} alt="" className="h-full w-full object-cover" />
                    : <div className="flex h-full items-center justify-center text-[0.55cqw] uppercase tracking-[0.3em] text-[#2C2B29]/25">+ Img</div>}
                </div>
                <div className="flex flex-col p-[5%]">
                  <p className="font-display text-[0.9cqw] font-bold text-[#2C2B29] line-clamp-1">{it.title || "Sin título"}</p>
                  {it.meta && (
                    <p className="mt-[2%] font-display text-[0.5cqw] font-semibold uppercase tracking-[0.3em] text-[#941620] line-clamp-1">
                      {it.meta}
                    </p>
                  )}
                  {it.body && (
                    <p className="mt-[4%] font-editorial text-[0.62cqw] leading-tight text-[#2C2B29]/75 line-clamp-4">
                      {it.body}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}


// =================================================================
// 16 · MOOD PHOTO — imagen pura sin texto (slide silencioso)
//     Solo muestra la imagen a sangre con un sutil número de página.
//     Ideal entre secciones para que el dossier respire.
// =================================================================
function MoodPhoto({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#2C2B29]">
      {img ? (
        <img src={imgUrl(img)} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f1f30] via-[#2C2B29] to-[#3a2f31]" />
      )}
      {/* Sin tint, sin texto, sin nada — solo la imagen.
          La marca de agua y el número de página se mantienen sutiles. */}
      <Footer project={project} light pageRef={pageRef} showWatermark={showWatermark} />
    </div>
  );
}

// =================================================================
// 17 · CHAPTER DIVIDER — separador grande de capítulo
//     Slide de transición con un nº de capítulo + nombre.
//     Estilo libro, paleta sand, mucho espacio en blanco.
// =================================================================
function ChapterDivider({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim() || (project?.title || "");
  return (
    <Chrome>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-[10%]">
        <p className="font-mono text-[0.7cqw] uppercase tracking-[0.45em] text-[#2C2B29]/40">
          {(project?.title || "").toUpperCase()}
        </p>
        <div className="my-[2cqw] flex items-center gap-[2cqw]">
          <div className="h-[1px] w-[5cqw] bg-[#941620]" />
          <p className="font-mono text-[1.2cqw] font-bold text-[#941620]">
            CAP · {String(pageRef || 1).padStart(2, "0")}
          </p>
          <div className="h-[1px] w-[5cqw] bg-[#941620]" />
        </div>
        <h2 className={`text-center font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
          {sectionTitle}
        </h2>
        {section?.text && (
          <p className="mt-[2cqw] max-w-[60%] text-center font-editorial text-[0.9cqw] italic leading-[1.6] text-[#2C2B29]/65 line-clamp-3">
            {section.text.split("\n")[0]}
          </p>
        )}
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 18 · TEAM INDIVIDUAL — un miembro del equipo (foto retrato + bio)
//     Layout 50/50: foto vertical a la izquierda + texto a la derecha
//     con nombre grande + rol + bio. Uno por persona, no grid.
// =================================================================
function TeamIndividual({ project, section, pageRef, showWatermark }) {
  const img = (section?.images || [])[0];
  const sectionTitle = (section?.label || "").trim();
  // Extraer rol de la primera línea si tiene "·" o ":"
  const lines = (section?.text || "").split("\n").filter(Boolean);
  let role = "";
  let bio = section?.text || "";
  if (lines[0] && lines[0].length < 80 && /[·:|]/.test(lines[0])) {
    role = lines[0].replace(/^.*?[·:|]\s*/, "").trim();
    bio = lines.slice(1).join("\n");
  }
  return (
    <Chrome>
      <div className="absolute inset-0 flex">
        <div className="relative overflow-hidden bg-[#2C2B29]" style={{ flex: "0 0 42%" }}>
          {img ? (
            <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-[0.7cqw] uppercase tracking-[0.4em] text-white/30">
              Foto del retrato
            </div>
          )}
        </div>
        <div className="flex flex-col justify-center bg-[#FAFAF6] px-[5cqw] py-[6cqw]"
             style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
          <h2 className={`mt-[0.8cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
            {sectionTitle}
          </h2>
          {role && (
            <p className="mt-[0.7cqw] font-display text-[0.7cqw] font-semibold uppercase tracking-[0.35em] text-[#941620]">
              {role}
            </p>
          )}
          <Rule />
          <p className="mt-[1.5cqw] font-editorial text-[0.85cqw] leading-[1.7] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[10]">
            {bio || "Sin bio aún."}
          </p>
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 17 · TIMELINE — línea horizontal de hitos con fecha + título + body
// =================================================================
// Lee hitos desde items[] (campos: meta=fecha, title, body), o como fallback
// desde text[] partiendo por líneas tipo "YYYY — Título: descripción".
function Timeline({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  // Construir lista de hitos desde items[] preferentemente
  let milestones = [];
  if ((section?.items || []).length > 0) {
    milestones = section.items.slice(0, 6).map((it) => ({
      date: (it.meta || "").trim(),
      title: (it.title || "").trim(),
      body: (it.body || "").trim(),
    }));
  } else {
    // Fallback: parsear el texto buscando "AAAA — Título: descripción"
    const lines = (section?.text || "")
      .split(/\n+/)
      .map((l) => l.trim())
      .filter(Boolean);
    milestones = lines.slice(0, 6).map((l) => {
      const m = l.match(/^([0-9]{4}(?:-[0-9]{2})?|\w{3,4}\.?\s*[0-9]{4}|[A-Z][a-z]+\s+[0-9]{4})\s*[—\-:·|]\s*(.+)$/);
      if (m) {
        const rest = m[2];
        const colon = rest.indexOf(":");
        return colon > 0
          ? { date: m[1].trim(), title: rest.slice(0, colon).trim(), body: rest.slice(colon + 1).trim() }
          : { date: m[1].trim(), title: rest.trim(), body: "" };
      }
      return { date: "", title: l, body: "" };
    });
  }
  const count = milestones.length;
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
        <h2 className={`mt-[0.6cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
          {sectionTitle || "Línea de tiempo"}
        </h2>
        <Rule />
        {count === 0 ? (
          <p className="mt-[3cqw] text-center text-[0.8cqw] uppercase tracking-[0.3em] text-[#2C2B29]/30">
            Añade hitos en items[] o en el texto (uno por línea).
          </p>
        ) : (
          <div className="relative mt-[3cqw] flex h-[55%] items-stretch">
            {/* Línea horizontal */}
            <div className="absolute left-0 right-0 top-[1.2cqw] h-[1px] bg-[#2C2B29]/25" />
            {milestones.map((m, i) => (
              <div key={i} className="relative flex flex-1 flex-col items-center px-[1cqw]">
                {/* Punto */}
                <div className="z-10 h-[2.4cqw] w-[2.4cqw] rounded-full border-[0.3cqw] border-[#941620] bg-white" />
                {/* Fecha */}
                {m.date && (
                  <p className="mt-[1cqw] font-mono text-[0.65cqw] font-semibold uppercase tracking-[0.22em] text-[#941620]">
                    {m.date}
                  </p>
                )}
                {/* Título */}
                <p className="mt-[0.6cqw] text-center font-display text-[0.95cqw] font-bold uppercase leading-[1.15] tracking-tight text-[#2C2B29] line-clamp-2">
                  {m.title}
                </p>
                {/* Body */}
                {m.body && (
                  <p className="mt-[0.5cqw] text-center font-editorial text-[0.62cqw] leading-[1.4] text-[#2C2B29]/70 line-clamp-3">
                    {m.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 18 · COMPARISON — dos cajas verticales lado a lado (antes/después, X vs Y)
// =================================================================
// Lee las 2 cajas desde items[0] e items[1] (title + body), o como fallback
// parte el texto por separador "---" o "\n\n---\n\n".
function Comparison({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  let left = { title: "", body: "" };
  let right = { title: "", body: "" };
  const items = section?.items || [];
  if (items.length >= 2) {
    left = { title: (items[0].title || "").trim(), body: (items[0].body || "").trim() };
    right = { title: (items[1].title || "").trim(), body: (items[1].body || "").trim() };
  } else {
    const parts = (section?.text || "").split(/\n*-{3,}\n*/);
    if (parts.length >= 2) {
      const a = parts[0].trim().split("\n");
      const b = parts[1].trim().split("\n");
      left = { title: a[0] || "", body: a.slice(1).join("\n").trim() };
      right = { title: b[0] || "", body: b.slice(1).join("\n").trim() };
    } else {
      left = { title: "Lado A", body: (section?.text || "").trim() };
      right = { title: "Lado B", body: "" };
    }
  }
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
        <h2 className={`mt-[0.6cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
          {sectionTitle || "Comparativa"}
        </h2>
        <Rule />
        <div className="mt-[2cqw] grid h-[72%] grid-cols-[1fr,2px,1fr] gap-[3%]">
          <ComparisonBox accent="#26775E" label="A" data={left} />
          <div className="self-stretch bg-[#2C2B29]/20" />
          <ComparisonBox accent="#941620" label="B" data={right} />
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}
function ComparisonBox({ accent, label, data }) {
  return (
    <div className="flex flex-col">
      <p className="font-mono text-[0.7cqw] font-bold uppercase tracking-[0.3em]" style={{ color: accent }}>
        {label}
      </p>
      <h3 className="mt-[0.4cqw] font-display text-[1.6cqw] font-bold uppercase leading-[1.05] tracking-tight text-[#2C2B29] line-clamp-2">
        {data.title || "—"}
      </h3>
      <div className="mt-[1cqw] h-[2px] w-[10%]" style={{ background: accent }} />
      <p className="mt-[1.2cqw] font-editorial text-[0.78cqw] leading-[1.6] text-[#2C2B29]/85 whitespace-pre-wrap line-clamp-[10]">
        {data.body || "—"}
      </p>
    </div>
  );
}

// =================================================================
// 19 · REFERENCES-GRID — 4 pósters/carteles grandes en grid 2×2 con texto mínimo
// =================================================================
// Pensada para referencias audiovisuales donde la IMAGEN es protagonista
// (carteles de películas). A diferencia de Cards, aquí el poster ocupa
// el 100% del card y el texto se superpone abajo con tint oscuro.
function ReferencesGrid({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  const items = (section?.items || []).slice(0, 4);
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <div className="flex items-end justify-between">
          <div>
            <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
            <h2 className={`mt-[0.6cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
              {sectionTitle || "Referencias"}
            </h2>
          </div>
          <p className="font-mono text-[0.6cqw] text-[#2C2B29]/40">
            {items.length} / {(section?.items || []).length}
          </p>
        </div>
        <Rule />
        <div className="mt-[1.5cqw] grid h-[75%] grid-cols-4 gap-[1.5%]">
          {Array.from({ length: 4 }).map((_, i) => {
            const it = items[i];
            if (!it) {
              return (
                <div key={i} className="flex items-center justify-center rounded-sm border border-dashed border-[#2C2B29]/15 bg-[#F1EEE5]/40 text-[0.5cqw] uppercase tracking-[0.3em] text-[#2C2B29]/25">
                  + Póster
                </div>
              );
            }
            return (
              <div key={i} className="relative overflow-hidden rounded-sm bg-[#2C2B29]">
                {it.image_url ? (
                  <img src={imgUrl({ url: it.image_url })} alt="" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2C2B29] to-[#3a3236]" />
                )}
                {/* Gradient tint inferior para el texto */}
                <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#2C2B29]/95 via-[#2C2B29]/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-[6%]">
                  <p className="font-display text-[0.85cqw] font-bold uppercase tracking-tight text-white line-clamp-2">
                    {it.title || "Sin título"}
                  </p>
                  {it.meta && (
                    <p className="mt-[3%] font-mono text-[0.55cqw] uppercase tracking-[0.22em] text-white/75 line-clamp-1">
                      {it.meta}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 20 · CREDITS-GRID — créditos del equipo con foto circular + rol
// =================================================================
// Pensada para una página tipo "El equipo" del dossier. Lee desde items[]:
// title=nombre, meta=rol, image_url=retrato. Max 6 personas en grid 3×2.
function CreditsGrid({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  const items = (section?.items || []).slice(0, 6);
  return (
    <Chrome>
      <div className="absolute inset-0 px-[5%] py-[5%]">
        <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
        <h2 className={`mt-[0.6cqw] font-display ${titleSize(sectionTitle)} font-extrabold uppercase leading-[0.95] tracking-tight text-[#2C2B29]`}>
          {sectionTitle || "Equipo"}
        </h2>
        <Rule />
        <div className="mt-[2cqw] grid h-[72%] grid-cols-3 grid-rows-2 gap-x-[3%] gap-y-[3%]">
          {Array.from({ length: 6 }).map((_, i) => {
            const it = items[i];
            if (!it) {
              return <div key={i} className="rounded-sm border border-dashed border-[#2C2B29]/10" />;
            }
            return (
              <div key={i} className="flex items-center gap-[5%]">
                {/* Foto circular */}
                <div className="relative h-[8cqw] w-[8cqw] flex-shrink-0 overflow-hidden rounded-full bg-[#F1EEE5] ring-1 ring-[#2C2B29]/15">
                  {it.image_url ? (
                    <img src={imgUrl({ url: it.image_url })} alt="" className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center font-display text-[2.5cqw] font-bold uppercase text-[#2C2B29]/30">
                      {((it.title || "").trim()[0] || "·").toUpperCase()}
                    </div>
                  )}
                </div>
                {/* Texto */}
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[0.95cqw] font-bold uppercase leading-tight tracking-tight text-[#2C2B29] line-clamp-2">
                    {it.title || "Sin nombre"}
                  </p>
                  {it.meta && (
                    <p className="mt-[2%] font-mono text-[0.55cqw] font-semibold uppercase tracking-[0.25em] text-[#941620] line-clamp-1">
                      {it.meta}
                    </p>
                  )}
                  {it.body && (
                    <p className="mt-[3%] font-editorial text-[0.6cqw] leading-[1.4] text-[#2C2B29]/65 line-clamp-3">
                      {it.body}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

// =================================================================
// 21 · DATA-CALLOUT — cifra protagonista gigantesca al centro
// =================================================================
// Pensada para destacar UN dato (ej. "104,2%", "27 días de rodaje").
// Lee la cifra desde la PRIMERA línea del texto. Si tiene formato "X | Y"
// usa Y como descriptor. El resto del texto va como contexto.
function DataCallout({ project, section, pageRef, showWatermark }) {
  const sectionTitle = (section?.label || "").trim();
  const lines = (section?.text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const firstLine = lines[0] || "—";
  // Si la primera línea tiene "valor | unidad" (ej. "104,2 | %") las separamos
  const pipeMatch = firstLine.match(/^(.+?)\s*\|\s*(.+)$/);
  const value = pipeMatch ? pipeMatch[1].trim() : firstLine;
  const unit = pipeMatch ? pipeMatch[2].trim() : "";
  const context = lines.slice(1).join(" ").trim();
  return (
    <Chrome>
      <div className="absolute inset-0 flex flex-col bg-[#FAFAF6]">
        {/* Cabecera con eyebrow + label */}
        <div className="px-[5%] pt-[5%]">
          <Eyebrow>{(project?.title || "").toUpperCase()}</Eyebrow>
          <p className="mt-[0.4cqw] font-display text-[0.8cqw] font-semibold uppercase tracking-[0.3em] text-[#2C2B29]/55">
            {sectionTitle || "Dato destacado"}
          </p>
        </div>
        {/* Cifra centrada */}
        <div className="flex flex-1 items-center justify-center px-[5%] text-center">
          <div>
            <p className="font-display text-[12cqw] font-extrabold leading-[0.9] tracking-tighter text-[#941620]">
              {value}
              {unit && (
                <span className="ml-[0.4cqw] font-display text-[3cqw] font-bold tracking-tight text-[#2C2B29]">
                  {unit}
                </span>
              )}
            </p>
            {context && (
              <p className="mx-auto mt-[2cqw] max-w-[70%] font-editorial text-[1cqw] leading-[1.5] text-[#2C2B29]/75 line-clamp-4">
                {context}
              </p>
            )}
          </div>
        </div>
        {/* Línea decorativa inferior */}
        <div className="mx-[5%] mb-[5%] h-[2px] bg-[#941620]/60" />
      </div>
      <Footer project={project} pageRef={pageRef} showWatermark={showWatermark} />
    </Chrome>
  );
}

